import type en from './en';

/**
 * German resources.
 *
 * Typed against the English object, so TypeScript fails the build if a key is
 * missing or renamed on one side only. `DeepPartial` is deliberately NOT used —
 * incomplete is exactly what we do not want.
 */
type Resources = {
  [NS in keyof typeof en]: { [K in keyof (typeof en)[NS]]: string };
};

const de: Resources = {
  common: {
    optionalParens: '(optional)',
    dashOptional: '— optional',
    unsaved: 'Nicht gespeichert',
    save: 'Speichern',
    cancel: 'Abbrechen',
    close: 'Schließen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    create: 'Anlegen',
    add: 'Hinzufügen',
    back: 'Zurück',
    search: 'Suchen',
    loading: 'Lädt…',
    saving: 'Speichern…',
    none: '—',
    optional: 'optional',
    required: 'Pflicht',
    yes: 'Ja',
    no: 'Nein',
    actions: 'Aktionen',
    status: 'Status',
    name: 'Name',
    email: 'E-Mail',
    role: 'Rolle',
    notes: 'Notizen',
    createdAt: 'Angelegt',
    updatedAt: 'Geändert',
    never: 'nie',
    show: 'Einblenden',
    hide: 'Ausblenden',
    copy: 'Kopieren',
    copied: 'Kopiert',
    download: 'Herunterladen',
    refresh: 'Aktualisieren',
    retry: 'Erneut versuchen',
    confirm: 'Bestätigen',
    unknownError: 'Da ist etwas schiefgegangen. Bitte erneut versuchen.',
    noResults: 'Nichts gefunden.',
    of: 'von',
    page: 'Seite',
    perPage: 'pro Seite',
    all: 'Alle',
    language: 'Sprache',
  },

  nav: {
    sectionOverview: 'Überblick',
    sectionBusiness: 'Geschäft',
    sectionOperations: 'Betrieb',
    dashboard: 'Dashboard',
    monitoring: 'Monitoring',
    bereitschaft: 'Bereitschaft',
    gitRegistry: 'Git & Registry',
    analytics: 'Analytics',
    sicherungsziele: 'Sicherungsziele',
    customers: 'Kunden',
    orders: 'Bestellungen',
    products: 'Produkte',
    applications: 'Applications',
    instances: 'Instanzen',
    apps: 'Shops und Apps',
    webseiten: 'Webseiten',
    fmMigration: 'FileMaker-Migration',
    servers: 'Server',
    inference: 'KI-Gateway',
    verbund: 'Verlagsverbund',
    domains: 'Domains',
    deployments: 'Deployments',
    releases: 'Releases',
    feedback: 'Feedback-Briefkasten',
    subtitle: 'Provisioning Center',
  },

  eyebrow: {
    pcSettings: 'PC-Einstellungen',
    platformAdministration: 'Verwaltung der Plattform',
    users: 'Nutzer',
    usersDescription: 'Mitarbeitende, Rollen, Sperren',
    integrations: 'Integrationen',
    integrationsDescription: 'Cloud-Anbieter, WooCommerce, Verwaltungsadressen',
    apiKeys: 'API-Keys',
    apiKeysDescription: 'Zugänge für CLI, MCP und Automatisierung',
    embedKeys: 'Embed-Keys',
    embedKeysDescription: 'Schlüssel für die Preis-Widgets',
    baseStacks: 'Base-Stacks',
    baseStacksDescription: 'Geteilte Infrastruktur pro Host — Proxy, Sync, Monitoring',
    auditLog: 'Audit-Log',
    auditLogDescription: 'Wer hat was wann geändert',
    changePassword: 'Passwort ändern',
    twoFactor: 'Zwei-Faktor-Authentifizierung',
    registryCredential: 'Mein Registry-Zugang',
    sshKeys: 'Mein SSH-Zugang',
    signOut: 'Abmelden',
  },

  roles: {
    ADMIN: 'Administrator',
    OPERATIONS: 'Operations',
    PRODUCT_MANAGER: 'Product Management',
    SUPPORT: 'Technischer Support',
    SALES: 'Vertrieb',
    BILLING: 'Buchhaltung',
  },

  noAccess: {
    title: 'Kein Zugriff auf diesen Bereich',
    restrictedTo: 'Diese Seite ist {{roles}} vorbehalten.',
    yourRole: 'Dein Konto hat die Rolle {{role}}.',
    askAdmin:
      'Wenn du den Zugriff brauchst, kann ein Administrator deine Rolle unter PC-Einstellungen › Nutzer ändern.',
  },

  tables: {
    search: 'Suchen…',
    filterAll: 'alle',
    clearFilters: '{{shown}} von {{total}} — Filter aufheben',
    range: '{{von}}–{{bis}} von {{gesamt}}',
    perPage: 'Zeilen je Seite',
    perPageOption: '{{n}} je Seite',
    prevPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    noMatch: 'Nichts passt zu Suche und Filter.',
    created: 'Angelegt',
    provider: 'Anbieter',
    exceptDecommissioned: 'alles außer stillgelegt',
    shared: 'Geteilt',
    dedicated: 'Dediziert',
    customerOwned: 'Kundeneigen',
    searchServers: 'Hostname, IP, Anbieter…',
    customer: 'Kunde',
    product: 'Produkt',
    plan: 'Tarif',
    domain: 'Domain',
    server: 'Server',
    serverHosting: 'Server / Hosting',
    deployed: 'Ausgerollt',
    type: 'Typ',
    instance: 'Instanz',
    duration: 'Dauer',
    retries: 'Versuche',
    timestamp: 'Zeitpunkt',
    user: 'Nutzer',
    action: 'Aktion',
    entityType: 'Objekttyp',
    entityId: 'Objekt-ID',
    ip: 'IP',
    automated: 'Automatisch',
    prefix: 'Präfix',
    lastUsed: 'Zuletzt verwendet',
    wcOrder: 'WC-Bestellung',
    payment: 'Zahlung',
    paid: 'Bezahlt',
    hostname: 'Hostname',
    sshKey: 'SSH-Key',
    slots: 'Slots',
    hardware: 'CPU / RAM / Disk',
    assignment: 'Zuordnung',
  },

  filters: {
    allStatusesEn: 'Alle Status',
    allStatuses: 'Alle Status',
    allTypes: 'Alle Typen',
    allProducts: 'Alle Produkte',
    allEntityTypes: 'Alle Objekttypen',
    searchDomain: 'Nach Domain suchen…',
    filterAction: 'Nach Aktion filtern…',
  },

  empty: {
    deployments: 'Noch keine Deployment-Jobs.',
    instances: 'Noch keine Instanzen.',
    orders: 'Noch keine Bestellungen.',
    audit: 'Noch keine Audit-Einträge.',
    customerUsers: 'Noch keine Portal-Zugänge.',
    servers: 'Noch keine Server. Über „Server hinzufügen" anlegen.',
  },

  dashboard: {
    recentDeployments: 'Letzte Deployment-Jobs',
  },

  products: {
    archive: "Archivieren",
    archiveConfirm: "Produkt „{{name}}\" archivieren?\n\nEs verschwindet aus dem Angebot; alles Laufende bleibt unberührt.",
    deleteConfirm: "Produkt „{{name}}\" endgültig löschen?\n\nEs läuft keine Instanz darauf. Tarife, Compose-Vorlagen und Releases gehen mit. Bestellungen bleiben bestehen, verlieren aber den Produktbezug.",
    notFound: 'Produkt nicht gefunden.',
    notSynced: 'noch nicht abgeglichen',
    noPlans: 'Noch keine Tarife.',
    noTemplates: 'Noch keine Vorlagen.',
    showTemplate: 'Compose-Vorlage anzeigen',
    editProduct: 'Produkt bearbeiten',
    syncHint: 'Diese Felder gehen unverändert an das WooCommerce-Produkt.',
    imageUrl: 'Bild-URL',
    optionsCommaSeparated: 'Optionen (kommagetrennt)',
    addTemplate: 'Compose-Vorlage hinzufügen',
    composeTemplate: 'Compose-Vorlage (YAML)',
    envTemplate: 'Env-Vorlage',
    newProduct: 'Neues Produkt',
  },

  apiKeys: {
    shownOnce: 'Dieser Key wird nur jetzt angezeigt — danach nicht mehr abrufbar. Jetzt kopieren.',
    nameExample: 'z.B. Claude Agent, CI Pipeline',
    forCliAs: 'Für CLI/MCP als',
    orAsBearer: 'setzen, oder direkt als Bearer-Token:',
    nameLabel: 'Name *',
    namePlaceholder: 'z.B. Claude Agent, CI Pipeline',
    cliHintBefore: 'Für CLI/MCP als',
    cliHintAfter: 'setzen, oder direkt als Bearer-Token:',
  },

  help: {
    buttonTitle: 'Hilfe zu diesem Bereich',
  },

  registry: {
    refreshHint: 'Zwischenspeicher leeren und den Katalog neu holen',
    myAccount: 'Mein Registry-Zugang',
    chooseFromRegistry: 'Aus Registry wählen',
    enterManually: 'Manuell eingeben',
    loadingRepositories: 'Lade Repositories…',
    selectRepository: '— Repository wählen —',
    selectRepositoryFirst: 'Erst Repository wählen',
    loadingTags: 'Lade Tags…',
    tagsUnavailable: 'Tags nicht abrufbar',
    selectTag: '— Tag wählen —',
    notInCatalogOption: '{{repo}} (nicht im Katalog)',
    tagGoneOption: '{{tag}} (nicht mehr vorhanden)',
    unreachable: 'Registry nicht erreichbar{{detail}} — bitte manuell eintragen.',
    notConfigured:
      'Keine Registry hinterlegt. Unter PC-Einstellungen › Integrationen konfigurieren, dann lassen sich Images hier auswählen.',
    notInCatalogHint:
      '„{{repo}}" steht nicht im Katalog der Registry — vermutlich ein Fremd-Image oder gelöscht.',
    tagPlaceholder: 'Tag, z.B. 20260803-1929',
  },

  auth: {
    onlyNow: 'nur jetzt',
    passwordSet: 'Passwort gesetzt',
    redirectingToLogin: 'Sie werden zur Anmeldung weitergeleitet…',
    invalidLink: 'Ungültiger Link',
    setPassword: 'Passwort festlegen',
    qrForSetup: 'QR-Code zur Einrichtung',
    noScanner: 'Kein Scanner? Schlüssel manuell eingeben',
    emailSent: 'E-Mail versendet',
    minEightChars: 'mind. 8 Zeichen',
    confirmNewPassword: 'Neues Passwort bestätigen',
    signIn: 'Anmelden',
    signInSubtitle: 'Bei deinem Konto anmelden',
    verifying: 'Wird geprüft…',
    mfaCodeLabel: 'Code aus der Authenticator-App',
    signingIn: 'Anmelden…',
    password: 'Passwort',
    forgotPassword: 'Passwort vergessen?',
    backToLogin: 'Zurück zur Anmeldung',
    invalidCredentials: 'E-Mail oder Passwort ist falsch.',
    accountInactive: 'Dieses Konto ist nicht aktiv. Bitte an einen Administrator wenden.',
    resetTitle: 'Passwort zurücksetzen',
    resetIntro:
      'Gib deine E-Mail-Adresse ein, wir senden dir einen Link zum Vergeben eines neuen Passworts.',
    resetSubmit: 'Link senden',
    resetSent:
      'Falls ein Konto zu dieser Adresse existiert, ist ein Link unterwegs. Bitte auch den Spam-Ordner prüfen.',
    newPasswordTitle: 'Neues Passwort vergeben',
    newPassword: 'Neues Passwort',
    repeatPassword: 'Passwort wiederholen',
    passwordsDoNotMatch: 'Die Passwörter stimmen nicht überein.',
    passwordTooShort: 'Das Passwort muss mindestens {{count}} Zeichen lang sein.',
    passwordChanged: 'Passwort geändert. Du kannst dich jetzt anmelden.',
    tokenInvalid: 'Dieser Link ist ungültig oder abgelaufen. Bitte einen neuen anfordern.',
    currentPassword: 'Aktuelles Passwort',
    changePasswordTitle: 'Passwort ändern',
    changePasswordSuccess: 'Passwort geändert.',
    mfaTitle: 'Zwei-Faktor-Authentifizierung',
    mfaEnterCode: 'Gib den sechsstelligen Code aus deiner Authenticator-App ein.',
    mfaCode: 'Code',
    mfaVerify: 'Prüfen',
    mfaInvalidCode: 'Dieser Code ist nicht gültig. Bitte erneut versuchen.',
    mfaBackupHint: 'Du kannst auch einen deiner Backup-Codes verwenden.',
    mfaSetupTitle: 'Zwei-Faktor-Authentifizierung einrichten',
    mfaSetupScan:
      'Scanne diesen QR-Code mit Google Authenticator, Microsoft Authenticator oder 1Password.',
    mfaSetupManual: 'Scannen nicht möglich? Diesen Schlüssel manuell eintragen:',
    mfaSetupConfirm: 'Zum Abschluss mit einem Code aus der App bestätigen.',
    mfaEnabled: 'Zwei-Faktor-Authentifizierung ist aktiv.',
    mfaDisabled: 'Zwei-Faktor-Authentifizierung ist aus.',
    mfaEnable: 'Aktivieren',
    mfaDisable: 'Deaktivieren',
    mfaBackupCodesTitle: 'Backup-Codes',
    mfaBackupCodesIntro:
      'Bewahre sie sicher auf. Jeder funktioniert einmal und lässt dich anmelden, wenn dein Telefon nicht verfügbar ist. Sie werden nur jetzt angezeigt.',
    mfaBackupCodesRemaining: 'Noch {{count}} Backup-Codes',
    mfaDisableConfirm:
      'Zwei-Faktor-Authentifizierung abschalten? Dein Konto ist danach nur noch durch das Passwort geschützt.',
  },

  forms: {
    slugRequired: 'Slug *',
    productRequired: 'Produkt *',
    selectProduct: 'Produkt wählen…',
    versionRequired: 'Version *',
    chooseCustomer: '— Kunde wählen —',
    optionalRecommended: '— optional, aber empfohlen',
    ifKeyProtected: '(falls Key damit geschützt)',
    emptyIfNoPassword: 'Leer lassen wenn kein Passwort',
    privateSshKey: 'Privater SSH-Key (PEM)',
    nameRequired: 'Name *',
    exampleNaboo: 'z. B. naboo',
    sizeRequired: 'Größe *',
    diskGb: 'Platte (GB)',
    gp3Note: 'gp3, wird beim Beenden mitgelöscht.',
    exampleHoth: 'z. B. hoth',
    serverTypeRequired: 'Server-Typ *',
    imageRequired: 'Image *',
    noImageLookup: 'Kein Lookup verfügbar — z.B. ubuntu-24.04, debian-12',
    vpsPlanRequired: 'VPS-Plan (Catalog Item) *',
    osTemplateRequired: 'Template (OS) *',
    dataCenterRequired: 'Data Center *',
    publicKey: 'Public Key',
    privateKeyPem: 'Private Key (PEM)',
    passphraseOptional: 'Passphrase (optional)',
    maxSlots: 'Max. Slots',
    hostnameRequired: 'Hostname *',
    ipRequired: 'IP-Adresse *',
    initialStatus: 'Initialer Status',
    statusReady: 'READY — bereit für Deployments',
    statusActive: 'ACTIVE — aktiv in Betrieb',
    statusProvisioning: 'PROVISIONING — wird eingerichtet',
    statusMaintenance: 'MAINTENANCE — Wartungsmodus',
    exampleRegion: 'z.B. nbg1, eu-west',
    diskGbPlain: 'Disk (GB)',
    exampleGpu: 'z. B. NVIDIA L4',
    costPerMonth: 'Kosten/Monat (€)',
  },

  servers: {
    replaceKey: "Ersetzen",
    storeKey: "Key hinterlegen",
    removeKey: "Zugang entfernen",
    removeKeyConfirm: "Unseren SSH-Schlüssel für „{{host}}\" entfernen?\n\nDie Maschine bleibt, wie sie ist — nur der Provisioning Center verliert seinen Zugang. Ausrollen und Hostprüfung gehen von hier aus nicht mehr.",
    hostCheck: 'Host-Prüfung',
    checkNotPossible: 'Prüfung nicht möglich',
    rolloutBaseStacks: 'Base-Stacks ausrollen',
    hostCheckHint: 'Erfüllt der Host alle Bedingungen für ein Deploy?',
    baseStackHint: 'Gemeinsame Infrastruktur auf diesem Host ausrollen oder ansehen',
  },

  activation: {
    newState: 'Neuer Status',
    stateActive: 'Aktiv — alle Einschränkungen aufheben',
    stateBlocked: 'Gesperrt — startet die Stufenleiter',
    stateTerminated: 'Vertrag beendet — Stufenleiter, Endstufe',
    reasonExample: 'z.B. Rechnung 2026-0412 seit 45 Tagen offen, dritte Mahnung raus',
    noticeTitleExample: 'Titel, z.B. „Zugang eingeschränkt — offene Rechnung“',
    noticeBodyHint:
      'Was ist zu tun, bis wann, wer ist Ansprechpartner. Bitte auch erwähnen, dass Datenexport weiter möglich ist.',
    lastContact: 'Letzter Kontakt',
    reportedVersion: 'Gemeldete Version',
    tokenValidUntil: 'Token gültig bis',
    stateAccordingToInstance: 'Status laut Instanz',
    recipients: 'Empfänger',
    adminsOnly: 'Nur Admins der Instanz',
    allUsers: 'Alle Nutzer',
    messageBody: 'Text der Nachricht',
    noActiveNotices: 'Keine aktiven Nachrichten.',
    systemGenerated: 'Vom System aus dem Zahlungsstatus erzeugt',
    withdrawNotice: 'Nachricht zurückziehen (bleibt zur Nachvollziehbarkeit gespeichert)',
    activationKey: 'Aktivierungsschlüssel',
  },

  embed: {
    copyToClipboard: 'In die Zwischenablage kopieren',
    newKey: 'Neuen Embed-Key anlegen',
    exampleName: 'Marketing juni.com',
    noRequestsYet: 'Über diesen Key kam noch keine Anfrage.',
  },

  customers: {
    noLogins: 'Noch keine Zugänge.',
    sendPasswordLink: 'Passwort-Link senden',
    accountInfo: 'Kontodaten',
    billingEmail: 'E-Mail für Rechnungen',
    technicalEmail: 'E-Mail für Technisches',
    wcCustomerId: 'WooCommerce-Kundennummer',
    reverseCharge: 'Reverse-Charge',
    noOrders: 'Keine Bestellungen.',
    noInstances: 'Keine Instanzen.',
    searchPlaceholder: 'Nach Firma oder E-Mail suchen…',
    terminated: 'Gekündigt',
    addressLine2Example: 'z.B. c/o, Abteilung',
    shownOnInvoice: 'Wird auf der Rechnung ausgewiesen',
  },

  instances: {
    open: 'Öffnen',
    address: 'Adresse',
    noAddress: 'keine Domain hinterlegt',
    keySet: '· Schlüssel',
    selfHostedHint: 'Läuft auf Hardware des Kunden. Kein SSH-Zugriff garantiert — Verwaltung bleibt dort.',
    noLink: 'Keine Verknüpfung.',
    noKeyYet: '· noch kein Schlüssel',
    notFound: 'Instanz nicht gefunden.',
    linkedInstances: 'Verknüpfte Instanzen',
    info: 'Instanzdaten',
    noJobs: 'Noch keine Ausrollaufträge.',
    secretKeys: 'Geheimnisse',
    secretsHint: 'Die Werte sind verschlüsselt und werden nie angezeigt.',
    noSecrets: 'Noch keine Geheimnisse.',
    deleteSecret: 'Geheimnis löschen',
    secretValue: 'Wert des Geheimnisses',
    currentConfig: 'Aktuelle Konfiguration',
  },

  baseStacks: {
    slug: 'Slug',
    ports: 'Ports',
    variable: 'Variable',
    origin: 'Herkunft',
    valueForHost: 'Wert für diesen Host',
    checkLabel: 'Prüfung',
    statusActive: 'ACTIVE — auswählbar',
    suggestionOverridable: 'Vorschlag, pro Server überschreibbar',
    statusDraft: 'DRAFT — noch nicht anbieten',
    statusDeprecated: 'DEPRECATED — nicht mehr anbieten',
    externalNetworks: 'Externe Netze',
    ownValueStored: 'Für diesen Host wurde ein eigener Wert gespeichert; die Ableitung greift nicht mehr.',
    tickedByDefault: 'Wird bei neuen Servern standardmäßig angehakt',
    stopKeepVolumes: 'Stack herunterfahren, Volumes behalten',
    hostNotReady: 'Der Host ist nicht bereit',
  },

  releases: {
    workerImage: 'Drittes Image (bei ojfm: Mustang) — leer heißt: wie das Backend',
    version: 'Version',
    channel: 'Kanal',
    migration: 'Migration',
    breaking: 'Bruch',
    approvedBy: 'Freigegeben von',
    name: 'Release-Name',
    none: 'Keine Releases gefunden.',
    notes: 'Release-Notizen',
    new: 'Neues Release',
  },

  tenants: {
    enabledModules: 'Freigeschaltete Module',
    doNotPreset: 'nicht vorgeben',
    displayNameExample: 'Anzeigename, z. B. Edition Halbig',
    codeHint: 'Code — leer: aus dem Namen',
  },

  users: {
    you: '(Sie)',
    registryAccounts: 'Registry-Zugänge',
    newUser: 'Neuen Nutzer anlegen',
    noActivity: 'Noch keine Aktivität aufgezeichnet.',
  },

  docker: {
    restart: 'Neu starten',
    restartsSinceCreation: 'Neustarts seit dem Anlegen des Containers',
    notFromPc: 'Nicht über das Provisioning Center ausgerollt',
  },

  orders: {
    setStatus: "Status setzen…",
    setStatusConfirm: "Bestellstatus auf {{status}} setzen?\n\nNormalerweise kommt der aus WooCommerce. Von Hand nur ändern, wenn der automatische Weg nicht funktioniert hat.",
  },
  providerCredentials: {
    title: 'Anbieter-Zugänge',
    explain:
      'Optional. Mit dem eigenen API-Schlüssel des Kunden bestellen und betreiben wir die Maschine in seinem Konto — Vertrag und Rechnung bleiben bei ihm. Ohne einen solchen gilt unser Haus-Zugang.',
    noneTitle: 'Kein Zugang hinterlegt',
    noneBody: 'Es gilt der Haus-Zugang. Alles funktioniert bis auf das, was die Anbieter-API braucht — Snapshots vor allem.',
    add: 'Schlüssel hinterlegen',
    replace: 'Ersetzen',
    tokenLabel: 'API-Token',
    tokenHint: 'Hier einmal sichtbar, danach verschlüsselt. Es taucht in keiner Antwort und in keinem Protokoll wieder auf.',
    labelField: 'Notiz (z. B. Projektname)',
    save: 'Speichern und prüfen',
    saving: 'Prüft…',
    cancel: 'Abbrechen',
    check: 'Jetzt prüfen',
    checking: 'Prüft…',
    checkOk: 'Schlüssel trägt — {{count}} Maschinen sichtbar.',
    remove: 'Entfernen',
    removeConfirm: 'Hinterlegten Schlüssel für {{provider}} entfernen?\n\nBeim Anbieter bleibt er gültig — widerrufen kann ihn nur der Kunde selbst.',
    lastUsed: 'zuletzt benutzt {{when}}',
    neverUsed: 'nie benutzt',
    lastChecked: 'geprüft {{when}}',
    neverChecked: 'noch nicht geprüft',
    scopeHetzner: 'Ein Hetzner-Token gilt für ein Projekt. Den Kunden um ein Token aus einem eigens für uns angelegten Projekt bitten — dann sehen wir von seinem Konto nichts weiter.',
    scopeHostinger: 'Ein Hostinger-Token hat die Rechte seines Besitzers: keine Projekte, keine Scopes. Es ist faktisch Vollzugriff auf das Konto samt Abrechnung und Domains. Beim Erfragen dazusagen.',
    scopeRobot: 'Robot ist eine andere Schnittstelle als die Cloud-API und hat eine eigene Anmeldung: einen Webservice-Benutzer, angelegt unter „Einstellungen → Webservice und App-Einstellungen". Nicht die Kontoanmeldung. Drei Fehlversuche sperren unsere IP für zehn Minuten — für alle Kunden. Also nicht auf Verdacht wiederholen.',
    robotUser: 'Webservice-Benutzer',
    robotPassword: 'Passwort',
  },
  snapshots: {
    title: 'Snapshots',
    explain:
      'Die ganze Maschine, über die Anbieter-Schnittstelle — ohne SSH, funktioniert also auch dann noch, wenn die Maschine nicht mehr hochkommt. Absturzrein, nicht anwendungsrein: ersetzt keine Datensicherung.',
    notPossible: 'Für diese Maschine nicht möglich',
    sourceCustomer: 'über das Konto des Kunden',
    sourceHouse: 'über unseren Haus-Zugang',
    none: 'Noch kein Snapshot.',
    create: 'Snapshot anlegen',
    creating: 'Startet…',
    descriptionPlaceholder: 'Wofür ist dieser Snapshot?',
    onlyOneWarning: 'Dieser Anbieter führt genau einen Snapshot je Maschine. Ein neuer verwirft den vorhandenen.',
    overwriteConfirm: 'Vorhandenen Snapshot ersetzen?',
    delete: 'Löschen',
    deleteConfirm: 'Diesen Snapshot löschen?',
    restore: 'Zurücksetzen',
    restoreTitle: 'Auf diesen Snapshot zurücksetzen',
    restoreWarning: 'Das überschreibt die ganze Platte. Jede Instanz auf dieser Maschine ({{count}}) wird mit zurückgesetzt.',
    restoreTypeToConfirm: 'Zur Bestätigung die Zahl der Instanzen eintippen:',
    restoreGo: 'Jetzt zurücksetzen',
    size: '{{gb}} GB',
    expires: 'läuft ab {{when}}',
  },
  robot: {
    title: 'Dedizierte Maschinen',
    explain: 'Hetzner AX- und EX-Server. Eine andere Schnittstelle als die Cloud — und ohne Snapshots, was eine Eigenschaft des Produkts ist und keine fehlende Anbindung hier.',
    pickCustomer: 'Aus wessen Konto?',
    loading: 'Frage Robot…',
    none: 'Keine dedizierten Maschinen in diesem Konto.',
    alreadyHere: 'schon im PC',
    cancelled: 'gekündigt, läuft bis {{until}}',
    take: 'Übernehmen',
    taking: 'Übernehme…',
    open: 'Dedizierte Maschine übernehmen',
    ownership: 'Gehört',
    ownershipCustomer: 'dem Kunden',
    ownershipJuni: 'uns, für diesen Kunden reserviert',
  },
  metrics: {
    title: 'Verläufe',
    unavailable: 'Keine Graphen verfügbar',
    fromHistory: 'Stundenwerte aus dem Verlaufsspeicher',
    limit: 'Grenze',
    since: 'seit {{when}}',
    silence: 'Stumm',
    silenceHint: 'Für 4 Stunden stumm — danach meldet er sich wieder, falls noch offen',
    silencedUntil: 'stumm bis {{when}}',
    unsilence: 'Wieder melden',
    openAlerts_one: '{{count}} offener Alarm',
    openAlerts_other: '{{count}} offene Alarme',
    open: 'Verläufe',
    noData: 'Keine Messwerte in diesem Zeitraum',
    noValue: 'keine Daten',
  },
  instanceStatus: {
    label: 'Status',
    active: 'Läuft',
    suspended: 'Pausiert',
    archived: 'Archiviert',
    terminated: 'Beendet',
    activeHint: 'In Betrieb.',
    suspendedHint: 'Vorübergehend aus — kommt wieder.',
    archivedHint: 'Aufbewahrt, nicht in Betrieb, ohne Rückkehr.',
    terminatedHint: 'Vertrag beendet.',
    machineState: 'Wird von einem Hintergrundauftrag gesetzt — hier nicht änderbar.',
    confirmTerminated: 'Auf „Beendet" setzen?\n\nDas ist das vertragliche Ende. Für eine Pause „Pausiert", zum Ablegen „Archiviert".',
  },
  environment: {
    label: 'Umgebung',
    production: 'Produktion',
    // Bewusst die englischen Kurzformen: so heißen sie im Namen der Instanz,
    // im Proxy und in `docker ps`. Zwei Wörter für dieselbe Sache sind eines
    // zu viel — und „Abnahme" erkennt niemand als das, was -stage bedeutet.
    staging: 'Stage',
    test: 'Test',
    demo: 'Demo',
    dev: 'Dev',
    unset: 'nicht festgelegt',
    productionHint: 'Trägt den echten Betrieb des Kunden.',
    stagingHint: 'Abnahme, bevor ein Release scharf geht.',
    testHint: 'Zum Ausprobieren — darf jederzeit kaputtgehen.',
    demoHint: 'Wird Interessenten gezeigt. Keine echten Daten.',
    devHint: 'Unsere eigene Entwicklung. Nicht für Kunden.',
  },
  images: {
    title: 'Bildtags',
    intro: 'Welches Image jeder Dienst beim nächsten Ausrollen zieht.',
    service: 'Dienst',
    tag: 'Tag',
    origin: 'Woher',
    fromInstance: 'hier gesetzt',
    fromRelease: 'Release',
    fromDefault: 'Vorgabe der Vorlage',
    fromOpen: 'nicht gesetzt',
    openHint: 'Kein Tag und keine Vorgabe — der Deploy hält hier an.',
    reset: 'Zurück aufs Release',
    resetHint: 'Entfernt den hier gesetzten Tag. Es gilt wieder das Release.',
    pick: 'Tag wählen',
    custom: 'Tag eintippen',
    deployNeeded: 'Gespeichert. Wirkt beim nächsten Ausrollen.',
    registryFailed: 'Registry nicht erreichbar — Tag von Hand eintippen.',
    noImages: 'Diese Instanz hat keine eigenen Images mit setzbarem Tag.',
    built: 'gebaut',
  },
  templateVersion: {
    label: 'Vorlage',
    unset: 'keine',
    active: 'aktuell',
    olderHint: 'Es gibt eine neuere Vorlage. Ein Wechsel wirkt beim nächsten Ausrollen.',
  },
  templateForm: {
    basedOn: 'Übernommen aus {{version}} — ändere, was zu ändern ist.',
    nothingToCopy: 'Für dieses Produkt gibt es noch keine Vorlage. Es beginnt leer.',
    carried: 'Wird unverändert übernommen',
    secrets: 'Erzeugte Geheimnisse',
    tenants: 'Mandanten',
    adminField: 'Feld der Admin-Adresse',
    modules: 'Module',
    warnEmpty: 'Ohne diese rollt eine ojfm-Instanz nicht aus — {{…:?required}} bricht ab.',
  },
  releaseImages: {
    title: 'Images',
    fromTemplate: 'Aus Vorlage {{version}} gelesen — eine Zeile je eigenem Image.',
    none: 'Dieses Produkt hat keine eigenen Images mit setzbarem Tag.',
    pick: 'Tag',
    empty: 'leer = wie das Backend',
    noTemplate: 'Das Produkt hat noch keine Vorlage.',
  },
  tenantsRead: {
    load: 'Aus der Instanz einlesen',
    loading: 'Wird gelesen…',
    hint: 'Holt die Mandanten, die dort wirklich stehen, samt ihren Codes. Auf den Code kommt es an: mit dem richtigen wird umbenannt, mit einem falschen ein zweiter Mandant angelegt.',
    found: '{{count}} gefunden — prüfen, dann speichern.',
    replaced: 'Die Liste unten wurde ersetzt. Gespeichert ist noch nichts.',
  },
  remove: {
    action: 'Entfernen',
    title: 'Instanz entfernen',
    subtitle: 'Baut die Container ab. Das lässt sich nicht rückgängig machen.',
    stepDown: 'docker compose down -v — Container und benannte Datenträger gehen',
    stepDir: 'Das Instanzverzeichnis auf dem Host wird gelöscht',
    stepDomain: 'Der Eintrag in der Domainübersicht wird entfernt',
    stepRecord: 'Der Datensatz bleibt als DELETED — Verlauf und Aufträge bleiben lesbar',
    dataLabel: 'Datenverzeichnisse mitlöschen',
    dataHint: 'Bind-Mounts (Datenbank, Uploads, Sicherungen) überleben den Abbau. Ohne Haken bleiben sie liegen — richtig so, wenn man zurückkommen könnte.',
    dataWarning: 'Die Datenbank dieser Instanz wird gelöscht. Angefasst werden nur Pfade, die den Stacknamen tragen.',
    typeToConfirm: 'Zum Bestätigen eintippen:',
    confirm: 'Entfernen',
    running: 'Wird eingereiht…',
    failed: 'Entfernen konnte nicht eingereiht werden.',
  },
  migrate: {
    action: 'Umziehen',
    title: 'Auf einen anderen Server umziehen',
    subtitle: 'Nimmt die Daten mit. Die Instanz steht währenddessen still.',
    pickTarget: 'Zielserver wählen',
    noSource: 'kein Server zugewiesen',
    checking: 'Wird geprüft…',
    dataTitle: '{{count}} Datenträger, zusammen {{size}}',
    noData: 'Nichts zum Übertragen gefunden.',
    unreadable: 'unlesbar',
    unreadableWarning: '{{count}} Datenträger sind für den SSH-Benutzer nicht lesbar. Sie würden leer übertragen, und der Auftrag meldete Erfolg. Vorher die Rechte richten.',
    checksTitle: 'Vorprüfung',
    whatHappens: 'Der Stack wird gestoppt, die Daten gehen über den PC zum Ziel, dort wird die Instanz neu ausgerollt. Auf der Quelle wird nichts gelöscht — der Rückweg ist ein Start auf dem alten Host.',
    blocked: 'Eine blockierende Prüfung ist fehlgeschlagen.',
    confirm: 'Umziehen',
    running: 'Wird eingereiht…',
    failed: 'Der Umzug konnte nicht eingereiht werden.',
  },
  tenantsSave: {
    done_one: 'Gespeichert — 1 Mandant hinterlegt.',
    done_other: 'Gespeichert — {{count}} Mandanten hinterlegt.',
    deployNeeded: 'Erreicht die Instanz beim nächsten Ausrollen.',
    retireNote: 'Was nicht auf der Liste steht, wird dort auf inaktiv gesetzt.',
  },
  headline: {
    releaseHint: 'Freigegebene Fassung dieser Instanz (Kanal: {{channel}}).',
    noReleaseHint: 'Kein Release gesetzt — das ist der Tag, den {{dienst}} zieht ({{herkunft}}).',
  },
  listenSicht: {
    aktiv: 'Aktiv',
    inaktiv: 'Inaktiv',
    alle: 'Alle',
  },
  templateCheck: {
    ok_one: 'Geprüft — 1 Dienst, nichts zu beanstanden.',
    ok_other: 'Geprüft — {{count}} Dienste, nichts zu beanstanden.',
    failed: 'So lässt sich die Vorlage nicht speichern:',
  },
  sql: {
    tab: 'SQL',
    title: 'SQL gegen die Datenbank dieser Instanz',
    intro: 'Was sonst über pgAdmin liefe. Jeder Aufruf steht im Protokoll — wer, wann, welche Anweisung.',
    read: 'Lesen',
    running: 'Läuft…',
    readOnlyHint: 'Nur lesend — Postgres weist jeden Schreibversuch ab.',
    writeMode: 'Schreiben zulassen',
    writeWarning: 'Erst ein Probelauf: er führt die Anweisung aus, zeigt wie viele Zeilen sie trifft, und macht am Ende rückgängig. Geschrieben wird erst danach, mit getipptem Stack-Namen.',
    dryRun: 'Probelauf (wird zurückgenommen)',
    write: 'Wirklich schreiben',
    aborted: 'abgebrochen',
    rows_one: '1 Zeile',
    rows_other: '{{count}} Zeilen',
    truncated: 'Gekürzt — es gibt mehr Zeilen, als hier stehen.',
    failed: 'Die Anweisung konnte nicht ausgeführt werden.',
    mode_lesen: 'Gelesen',
    mode_probelauf: 'Probelauf — zurückgenommen',
    mode_schreiben: 'Geschrieben',
  },
  secrets: {
    doNotRotate: 'nicht wechseln',
    typeKeyToDelete: 'Zum Löschen „{{key}}“ eintippen.',
  },
  dns: {
    label: 'DNS',
    pc: 'über den PC',
    beiwagen: 'Beiwagen im Stack',
    manuell: 'manuell',
    pcHint: 'Der PC setzt den A-Record beim Ausrollen und nach einem Umzug. Der Cloudflare-Token bleibt dabei auf dem PC.',
    beiwagenHint: 'Ein DDNS-Container im Stack hält den Eintrag alle fünf Minuten aktuell. Fängt einen Host ab, dessen Adresse sich von selbst ändert — braucht dafür den Token auf diesem Host, und dessen IP muss in der Sperrliste stehen.',
    manuellHint: 'Niemand von uns. Bei einer Kundendomain zeigt der Kunde selbst auf den Host.',
  },
  version: {
    copyHint: 'Versionsnummer kopieren',
    copied: 'kopiert',
    whatsNew: 'Was ist neu?',
    whatsNewWithVersion: 'Was ist neu? · v{{version}}',
    changelogEmpty: 'Für diese Fassung liegt keine Zusammenfassung vor.',
  },

  webstat: {
    tab: 'Statistik',

    // ─── Die Grenzen, gleich vorweg ─────────────────────────────────────────
    limitsTitle: 'Cookielose Messung',
    limits:
      'Cookielos erhoben, ohne Speicherung auf dem Gerät. Ein Besuch wird nur innerhalb eines ' +
      'Kalendertags zusammengefasst — danach ist derselbe Mensch nicht mehr wiedererkennbar. ' +
      'Es gibt hier deshalb keine Besucher, keine Wiederkehrer und keine Wege über mehrere ' +
      'Besuche. Alle Werte sind Untergrenzen: Wer den Zähler blockiert, wird nicht gezählt.',

    // ─── Zeiträume ──────────────────────────────────────────────────────────
    today: 'Heute',
    yesterday: 'Gestern',
    days7: '7 Tage',
    days28: '28 Tage',
    days90: '90 Tage',
    months12: '12 Monate',
    periodRange: '{{from}} bis {{to}}',

    // ─── Kennzahlen ─────────────────────────────────────────────────────────
    pageviews: 'Seitenaufrufe',
    visits: 'Tagesbesuche',
    visitsHint: 'Nicht Besucher. Wer an drei Tagen kommt, zählt dreimal.',
    viewsPerVisit: 'Aufrufe je Besuch',
    singlePage: 'Nur eine Seite',
    singlePageHint: 'der Besuche',
    duration: 'Verweildauer',
    durationHint: 'sichtbare Zeit, im Mittel',
    comparedToPrevious: 'gegenüber dem gleich langen Zeitraum davor',

    notCounted: 'Nicht gezählt: {{bots}} Bots, {{addresses}} von ausgeschlossenen Adressen.',
    notCountedBots: 'Nicht gezählt: {{bots}} Bots.',
    notCountedAddresses: 'Nicht gezählt: {{addresses}} von ausgeschlossenen Adressen.',

    // ─── Seiten ─────────────────────────────────────────────────────────────
    pages: 'Seiten',
    page: 'Seite',
    entries: 'Einstiege',
    exits: 'Ausstiege',
    withAction: '{{count}} m. Aktion',
    withActionHint:
      'Ausstiege, bei denen vorher etwas angeklickt wurde — auf einer Kontaktseite ist das das Ziel, nicht ein Verlust.',
    readTo: 'Gelesen bis',
    scrollHint:
      'Anteil der gemessenen Aufrufe, die 25/50/75/100 % erreicht haben — gemessen bei {{count}} Aufruf(en)',
    measuredOn: 'bei {{measured}} von {{total}}',
    measuredHint:
      'Die Dauer kommt beim Verlassen der Seite und geht manchmal verloren. Hochgerechnet wird sie nicht.',
    fitsViewport: 'passt ins Fenster',
    fitsViewportHint:
      'Die Seite passt ins Fenster — die Lesetiefe ist deshalb trivial bei 100 % und nicht vergleichbar.',
    noPages: 'Keine Seitenaufrufe in diesem Zeitraum.',

    // ─── Herkunft und Technik ───────────────────────────────────────────────
    originAndTech: 'Herkunft und Technik',
    referrer: 'Verweiser',
    campaign: 'Kampagne',
    source: 'Quelle',
    country: 'Land',
    device: 'Gerät',
    browser: 'Browser',
    system: 'System',
    value: 'Wert',
    noValues: 'Für dieses Merkmal liegt in diesem Zeitraum nichts vor.',

    // ─── Noch keine Zahlen ──────────────────────────────────────────────────
    noFigures: 'Noch keine Zahlen',

    // ─── Einrichtung ────────────────────────────────────────────────────────
    setup: 'Einrichtung',
    switchedOn: 'Die Reichweitenmessung ist eingeschaltet.',
    switchedOff: 'Die Reichweitenmessung ist ausgeschaltet.',
    switchOn: 'einschalten',
    switchOff: 'ausschalten',
    checkNow: 'jetzt nachsehen',
    checking: 'sehe nach…',
    checkNowHint:
      'Die Webseite jetzt abrufen und nachsehen, ob Zähl-Tag und Datenschutzabschnitt dort stehen',
    switchOnBlocked: 'Erst den Datenschutzabschnitt bestätigen',
    scriptLine: 'Diese Zeile gehört ins Markup der Webseite',
    scriptLinePreview: 'So wird der Zähler später eingebaut',
    scriptLineHint:
      'Die Kennung steht als STAT_ID in der .env der Instanz — im Markup gehört ein Platzhalter, kein fester Wert. So läuft derselbe Quellcode in Abnahme und Produktion. Auf der Fehlerseite zusätzlich data-fehler="404" setzen.',
    privacyText: 'Dieser Abschnitt gehört in die Datenschutzerklärung der Webseite',
    confirmPrivacy:
      'Der Abschnitt oben wird in die Datenschutzerklärung dieser Webseite übernommen. Ohne ihn darf nicht gemessen werden — der PC sieht wöchentlich nach, ob er dort steht.',
    switchOnFailed: 'Einschalten fehlgeschlagen.',
    checkFailed: 'Nachschau fehlgeschlagen.',
    copy: 'kopieren',
    copied: 'kopiert',

    // ─── Was die Nachschau ergeben hat ──────────────────────────────────────
    checkTitle: 'Nachschau auf der Webseite',
    tagMissing:
      'Auf der Startseite ist kein Zähl-Tag zu finden. Bis es eingebaut ist, wird nichts gezählt.',
    tagMissingSince:
      'Auf der Startseite ist kein Zähl-Tag zu finden (seit {{days}} Tagen). Bis es eingebaut ist, wird nichts gezählt.',
    privacyMissing:
      'In der Datenschutzerklärung fehlt der Abschnitt zur Reichweitenmessung. Ohne ihn sollte nicht gemessen werden.',
    privacyMissingSince:
      'In der Datenschutzerklärung fehlt der Abschnitt zur Reichweitenmessung (seit {{days}} Tagen). Ohne ihn sollte nicht gemessen werden.',

    // ─── Sitemap-Abgleich ───────────────────────────────────────────────────
    comparison: 'Abgleich mit der Sitemap',
    unvisited: 'In der Sitemap, nie aufgerufen',
    unvisitedHint:
      'Diese Seiten sollen gefunden werden und wurden in diesem Zeitraum kein einziges Mal geöffnet.',
    unlisted: 'Aufgerufen, aber nicht in der Sitemap',
    unlistedHint:
      'Entweder in der Sitemap vergessen — oder die Seiten hätten längst verschwinden sollen.',
    removedFromSitemap: 'nicht mehr in der Sitemap',
    sitemapState: 'Sitemap gelesen am {{when}}',
    sitemapNever:
      'Die Sitemap wurde noch nicht gelesen. Ohne sie kennt das Dashboard nur die Seiten, die aufgerufen wurden.',
    readSitemap: 'Sitemap jetzt lesen',
    reading: 'lese…',
    sitemapResult: '{{count}} Seiten gelesen, {{added}} neu, {{gone}} nicht mehr gelistet.',
    // ─── Ausleitung ─────────────────────────────────────────────────────────
    exportTitle: 'Export',
    exportHint:
      'Immer Tageswerte, unabhängig vom angezeigten Zeitraum — eine Zeile je Tag. Daraus lässt sich in der Tabelle jede Zusammenfassung bilden; umgekehrt nicht.',
    exportDays: 'Tageswerte',
    exportPages: 'Seiten je Tag',
    exportAttributes: 'Herkunft je Tag',
    exportComparison: 'Sitemap-Abgleich',
    exportFailed: 'Export fehlgeschlagen.',
    noComparison: 'Sitemap und Messung stimmen überein — auf keiner Seite bleibt etwas übrig.',
    lastChecked: 'zuletzt nachgesehen: {{when}}',
  },
};

export default de;
